import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import {
  useGetExtraChecklistConfig,
  useUpdateExtraChecklistConfig,
} from "@rentiohq/shared-frontend/dist/reduxV2/preferences/preference.hooks";
import { IExtraChecklistItem } from "@rentiohq/shared-frontend/dist/reduxV2/preferences/preference.types";
import { EPreferences } from "@rentiohq/shared-frontend/dist/types/preferences.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Button, Loading } from "@rentiohq/web-shared/dist/components";
import { useEffect, useRef, useState } from "react";
import {
  ChecklistSection,
  IChecklistSectionRef,
} from "./components/ChecklistSection";

const ChecklistPreferences = () => {
  const { data = [], isFetching } = useGetExtraChecklistConfig();
  const { update, isUpdating } = useUpdateExtraChecklistConfig();

  const entryRef = useRef<IChecklistSectionRef>(null);
  const exitRef = useRef<IChecklistSectionRef>(null);

  const [entryEnabled, setEntryEnabled] = useState(false);
  const [entryItems, setEntryItems] = useState<IExtraChecklistItem[]>([]);
  const [exitEnabled, setExitEnabled] = useState(false);
  const [exitItems, setExitItems] = useState<IExtraChecklistItem[]>([]);

  useEffect(() => {
    if (!data.length) {
      return;
    }

    const stringifiedEntryItems = data?.find(
      item => item.key === EPreferences.ChecklistExtraEntry,
    )?.json;

    const stringifiedExitItems = data?.find(
      item => item.key === EPreferences.ChecklistExtraExit,
    )?.json;

    if (stringifiedEntryItems) {
      const parsedEntryItems: {
        actions: IExtraChecklistItem[];
        enabled: boolean;
      } = JSON.parse(stringifiedEntryItems);
      const { enabled, actions } = parsedEntryItems;
      setEntryEnabled(enabled);
      setEntryItems(actions ?? []);
    }

    if (stringifiedExitItems) {
      const parsedExitItems = JSON.parse(stringifiedExitItems);
      const { enabled, actions } = parsedExitItems;
      setExitEnabled(enabled);
      setExitItems(actions ?? []);
    }
  }, [data]);

  const handleSave = () => {
    const entryValid = entryRef.current?.validateAll() ?? true;
    const exitValid = exitRef.current?.validateAll() ?? true;
    if (!entryValid || !exitValid) {
      return;
    }

    update({
      entry: { enabled: entryEnabled, actions: entryItems },
      exit: { enabled: exitEnabled, actions: exitItems },
    });
  };

  if (isFetching) {
    return <Loading />;
  }

  return (
    <div>
      <ChecklistSection
        ref={entryRef}
        title={getLocalizedText("settings.extra_checklist.entry.title")}
        description={getLocalizedText(
          "settings.extra_checklist.entry.description",
        )}
        enabled={entryEnabled}
        items={entryItems}
        onToggleEnabled={setEntryEnabled}
        onItemsChange={setEntryItems}
      />

      <Spacer weight={ESpacerWeight.W24} />

      <ChecklistSection
        ref={exitRef}
        title={getLocalizedText("settings.extra_checklist.exit.title")}
        description={getLocalizedText(
          "settings.extra_checklist.exit.description",
        )}
        enabled={exitEnabled}
        items={exitItems}
        onToggleEnabled={setExitEnabled}
        onItemsChange={setExitItems}
      />

      <Spacer weight={ESpacerWeight.W24} />

      <Button
        appearance="primary"
        isSubmitting={isUpdating}
        onClick={handleSave}
      >
        {getLocalizedText("system.save")}
      </Button>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default ChecklistPreferences;
